import {inject, Injectable} from '@angular/core';
import {
  LOG_LEVEL,
  Purchases
} from '@revenuecat/purchases-capacitor';
import {Capacitor} from '@capacitor/core';
import {
  CustomerInfo,
  PurchaseOptions,
  PurchaseProvider,
  PurchaseProviderConfig,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import {
  CapacitorSdkMapper
} from './capacitor-sdk-mapper';
import {
  CapacitorPurchasesAdapter
} from './capacitor-purchases-adapter';
import {
  CapacitorIosOfferResolver
} from './capacitor-ios-offer-resolver';
import {
  MetaAttributionService
} from '../meta-attribution/meta-attribution.service';
import {
  LoggerService
} from '../logging/logger.service';
import {
  ObservabilityService
} from '../observability/observability.service';

/**
 * Wraps the RevenueCat Capacitor SDK behind the
 * PurchaseProvider interface. Uses CapacitorSdkMapper
 * to convert SDK types to our interface types.
 */
@Injectable()
export class CapacitorPurchaseProvider
  implements PurchaseProvider {

  // RevenueCat publishable mobile API keys —
  // safe to include in client-side code
  private static readonly API_KEYS:
    Record<string, string> = {
      ios: 'appl_GJvVtzPCshLxjUlFJdCIRuZCMRI',
      android: 'goog_iRMBhhIJLJVcbWUMIVzkXQMATYJ'
    };

  private readonly metaAttribution =
    inject(MetaAttributionService);
  private readonly logger = inject(LoggerService);
  private readonly observability =
    inject(ObservabilityService);
  private readonly mapper =
    new CapacitorSdkMapper();
  private readonly adapter =
    inject(CapacitorPurchasesAdapter);
  private readonly iosOfferResolver =
    inject(CapacitorIosOfferResolver);

  /** Cache of native SDK products keyed by id */
  private readonly sdkProductCache =
    new Map<string, any>();

  private getApiKey(): string {
    const platform = Capacitor.getPlatform();
    const key =
      CapacitorPurchaseProvider.API_KEYS[platform];
    if (!key) {
      throw new Error(
        'Unsupported platform for Purchases: '
        + platform
      );
    }
    return key;
  }

  async configure(
    config: PurchaseProviderConfig
  ): Promise<void> {
    await Purchases.configure({
      apiKey: this.getApiKey(),
      ...(config.appUserID
        ? { appUserID: config.appUserID }
        : {})
    });
  }

  async isConfigured(): Promise<boolean> {
    const status =
      await Purchases.isConfigured();
    return status?.isConfigured ?? false;
  }

  async logIn(appUserID: string): Promise<void> {
    await Purchases.logIn({ appUserID });
  }

  async setLogLevel(
    level: string
  ): Promise<void> {
    const levelMap: Record<string, LOG_LEVEL> = {
      debug: LOG_LEVEL.DEBUG,
      info: LOG_LEVEL.INFO,
      warn: LOG_LEVEL.WARN,
      error: LOG_LEVEL.ERROR
    };
    const sdkLevel =
      levelMap[level] ?? LOG_LEVEL.ERROR;
    await Purchases.setLogLevel({
      level: sdkLevel
    });
  }

  async getOfferings():
    Promise<SubscriptionPackage[]> {
    const offerings =
      await this.adapter.getOfferings();
    const packages =
      offerings.current?.availablePackages ?? [];
    const mapped = packages.map(p => {
      const pkg = this.mapper.mapPackage(p);
      this.sdkProductCache.set(
        pkg.product.identifier, p.product
      );
      return pkg;
    });
    // The whole iOS discounted-offer story is resolved
    // here, invisibly to callers. Android/web keep the
    // shape-derived default from the mapper.
    if (Capacitor.getPlatform() === 'ios') {
      await this.iosOfferResolver.resolve(
        mapped, this.sdkProductCache
      );
    }
    return mapped;
  }

  async purchase(
    product: SubscriptionProduct,
    // Options unused on mobile — the native store
    // manages customer identity independently
    _options?: PurchaseOptions
  ): Promise<{ customerInfo: CustomerInfo }> {
    const sdkProduct =
      this.sdkProductCache.get(product.identifier);
    if (!sdkProduct) {
      throw new Error(
        'Product not found in cache. '
        + 'Call getOfferings() before purchase().'
      );
    }

    // Route on the DTO the user actually saw — never on
    // the signature cache, which is a signature store only.
    const result = product.effectiveOffer?.kind
      === 'promotional'
      ? await this.purchasePromotionalProduct(
        product, sdkProduct
      )
      : await this.adapter.purchaseStoreProduct({
        product: sdkProduct
      });

    return {
      customerInfo:
        this.mapper.mapCustomerInfo(
          result.customerInfo
        )
    };
  }

  /**
   * Re-signs the promotional offer immediately before purchase (signed
   * offers can expire). If re-signing fails, falls back to the
   * load-time signature cached by CapacitorIosOfferResolver. If there
   * is no signature at all, or the store rejects the one we have, this
   * throws — the purchase FAILS through the caller's existing
   * trackPurchaseFailed/errorService path. It must never silently
   * degrade to a plain purchase, which would charge the standard price
   * against a product the user was shown at a promotional price.
   */
  private async purchasePromotionalProduct(
    product: SubscriptionProduct,
    sdkProduct: any
  ) {
    const discount = sdkProduct.discounts?.[0];
    let offer = discount
      ? await this.adapter.getPromotionalOffer({
        product: sdkProduct, discount
      }).catch(error => {
        this.logger.warn(
          'Promotional offer re-sign failed at '
          + `purchase time for ${product.identifier}`
          + ', falling back to the load-time '
          + 'signature if available', error
        );
        this.observability
          .trackPromotionalOfferSigningFailed(
            product.identifier,
            error instanceof Error
              ? error.message : 'Unknown error',
            { source: 'purchase_time_resign' }
          );
        return undefined;
      })
      : undefined;

    if (!offer) {
      offer = this.iosOfferResolver
        .getCachedOffer(product.identifier);
    }
    if (!offer) {
      throw new Error(
        'No signed promotional offer available for '
        + product.identifier
      );
    }

    return this.adapter.purchaseDiscountedProduct({
      product: sdkProduct, discount: offer
    });
  }

  async addCustomerInfoUpdateListener(
    listener: (info: CustomerInfo) => void
  ): Promise<void> {
    await Purchases.addCustomerInfoUpdateListener(
      (info) => {
        listener(
          this.mapper.mapCustomerInfo(info)
        );
      }
    );
  }

  async restorePurchases(): Promise<{
    customerInfo: CustomerInfo;
  }> {
    const result =
      await Purchases.restorePurchases();
    return {
      customerInfo:
        this.mapper.mapCustomerInfo(
          result.customerInfo
        )
    };
  }

  // --- Code redemption ---

  isRedeemCodeAvailable(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  async redeemCode(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      await Purchases.presentCodeRedemptionSheet();
      return;
    }
    if (platform === 'android') {
      // No programmatic redemption on Google Play.
      // The component shows an explanation dialog.
      return;
    }
    throw new Error(
      'Code redemption not supported on platform: '
      + platform
    );
  }

  // --- Management ---

  isManagementAvailable(): boolean {
    return false;
  }

  async openManagementPage(): Promise<void> {
    /* no-op — mobile uses native store */
  }

  // --- Attribution ---

  async initialiseAttribution(): Promise<void> {
    try {
      await this.metaAttribution
        .enableAutoLogEvents();
      await Purchases.collectDeviceIdentifiers();

      const identifiers =
        await this.metaAttribution
          .getAttributionIdentifiers();
      if (identifiers.fbAnonymousId) {
        await Purchases.setFBAnonymousID({
          fbAnonymousID:
            identifiers.fbAnonymousId
        });
      }
    } catch (error) {
      this.logger.warn(
        'CapacitorPurchaseProvider: '
        + 'Attribution failed',
        error
      );
    }
  }

  async setAttributes(
    attributes: Record<string, string | null>
  ): Promise<void> {
    await Purchases.setAttributes(attributes);
  }

  async setFBAnonymousID(
    fbAnonymousId: string
  ): Promise<void> {
    await Purchases.setFBAnonymousID({
      fbAnonymousID: fbAnonymousId
    });
  }

  async collectDeviceIdentifiers():
    Promise<void> {
    await Purchases.collectDeviceIdentifiers();
  }
}
